import { html, css, LitElement } from 'lit';
import Hls from 'hls.js';

export class WhisperMedia extends LitElement {
  static get styles() {
    return css`
      audio {
        width: 100%;
      }
      video {
        max-height: 200px;
        margin-left: auto;
        margin-right: auto;
        display: block;
      }
    `;
  }

  static get properties() {
    return {
      audio: { type: String },
      video: { type: String }
    };
  }

  constructor() {
    super();
    this.hlsInstance = null;
    console.log('WhisperMedia v8 - simplified render for m3u8');
  }

  updateTime(time) {
    window.dispatchEvent(
      new CustomEvent("update-time", {
        detail: { time: time }
      })
    );
  }

  setupHLS(mediaElement, source) {
    // Clean up any existing HLS instance
    if (this.hlsInstance) {
      this.hlsInstance.destroy();
      this.hlsInstance = null;
    }

    const canPlayNative = mediaElement.canPlayType('application/vnd.apple.mpegurl');

    if (canPlayNative) {
      console.log('Using native HLS support for:', source);
      mediaElement.src = source;
    } else if (Hls.isSupported()) {
      console.log('Setting up HLS.js for:', source);

      this.hlsInstance = new Hls({
        debug: false,
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
        maxBufferLength: 30,
        maxMaxBufferLength: 600,
        startLevel: -1,
        capLevelToPlayerSize: true
      });

      this.hlsInstance.loadSource(source);
      this.hlsInstance.attachMedia(mediaElement);

      const self = this;

      this.hlsInstance.on(Hls.Events.MANIFEST_PARSED, function(event, data) {
        console.log('HLS manifest loaded successfully, levels:', data.levels.length);
      });

      this.hlsInstance.on(Hls.Events.LEVEL_LOADED, function(event, data) {
        console.log('HLS level loaded:', data.level);
      });

      this.hlsInstance.on(Hls.Events.ERROR, function(event, data) {
        console.error('HLS error:', data);

        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log('Fatal network error, attempting recovery...');
              if (self.hlsInstance) {
                self.hlsInstance.startLoad();
              }
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('Fatal media error, attempting recovery...');
              if (self.hlsInstance) {
                self.hlsInstance.recoverMediaError();
              }
              break;
            default:
              console.log('Fatal error, destroying HLS instance');
              self.destroyHls();
              mediaElement.src = source;
              break;
          }
        }
      });

      this.hlsInstance.on(Hls.Events.MEDIA_ATTACHED, function() {
        console.log('HLS media attached successfully');
      });

      this.hlsInstance.on(Hls.Events.FRAG_LOADED, function(event, data) {
        console.log('Fragment loaded:', data.frag.url);
      });

    } else {
      console.error('HLS.js is not supported in this browser, trying direct source');
      mediaElement.src = source;
    }
  }

  destroyHls() {
    if (this.hlsInstance) {
      this.hlsInstance.destroy();
      this.hlsInstance = null;
    }
  }

  async firstUpdated() {
    const videoEl = this.shadowRoot ? this.shadowRoot.querySelector('video') : null;
    const audioEl = this.shadowRoot ? this.shadowRoot.querySelector('audio') : null;
    const media = videoEl || audioEl;

    if (!media) return;

    const self = this;

    media.ontimeupdate = function() {
      self.updateTime(media.currentTime);
    };

    const handlePlayerTimeUpdate = function(e) {
      media.currentTime = e.detail.time;
    };

    window.addEventListener('update-player-time', handlePlayerTimeUpdate);
    media._playerTimeHandler = handlePlayerTimeUpdate;

    // Handle HLS for video
    if (this.video && videoEl && this.video.endsWith('.m3u8')) {
      this.setupHLS(videoEl, this.video);
    }

    // Handle HLS for audio
    if (this.audio && audioEl && this.audio.endsWith('.m3u8')) {
      this.setupHLS(audioEl, this.audio);
    }

    media.onerror = function(e) {
      console.error('Media loading error:', e);
      console.error('Media error details:', {
        error: media.error,
        networkState: media.networkState,
        readyState: media.readyState,
        src: media.src
      });
    };
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.destroyHls();

    const media = this.shadowRoot ? this.shadowRoot.querySelector('video, audio') : null;
    if (media && media._playerTimeHandler) {
      window.removeEventListener('update-player-time', media._playerTimeHandler);
    }
  }

  render() {
    if (this.audio) {
      const isM3U8 = this.audio.endsWith('.m3u8');
      const testAudio = document.createElement('audio');
      const canPlayNative = testAudio.canPlayType('application/vnd.apple.mpegurl');

      const src = (!isM3U8 || canPlayNative) ? this.audio : '';

      return html`<audio .src=${src} controls preload="auto"></audio>`;
    }

    if (this.video) {
      const isM3U8 = this.video.endsWith('.m3u8');
      const testVideo = document.createElement('video');
      const canPlayNative = testVideo.canPlayType('application/vnd.apple.mpegurl');

      const src = (!isM3U8 || canPlayNative) ? this.video : '';

      return html`<video .src=${src} controls preload="auto"></video>`;
    }

    return html``;
  }
}